import { useState, useEffect, useMemo } from "react";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import SearchField from "@components/ui/textfields/SearchField";
import BaseModal from "@components/modals/BaseModal";
import { StatusBlock } from "@components/ui/blocks/Status";
import { UserAvatar } from "@components/ui/UserAvatar";
import { useDataStore } from "@store/user_org";
import { invitationsApi } from "@services/api/invitations";
import { usersApi } from "@services/api";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { getHistoryDateFromFlutter } from "@services/timeHelpers";
import type {
  InvitationResponse,
  IntegrityReport,
  IntegrityIssue,
} from "@services/api/types/invitation";
import type { UserResponse } from "@services/api/types/user";

type StatusType = "step" | "success" | "ai" | "error" | "warning" | "info" | "default";
type Kind =
  | "accepted"
  | "pending"
  | "expired"
  | "email_error"
  | "email_unsent"
  | "cancelled";

const KIND_META: Record<Kind, { label: string; type: StatusType; problem: boolean }> = {
  accepted: { label: "Acceptée", type: "success", problem: false },
  pending: { label: "En attente", type: "info", problem: false },
  expired: { label: "Expirée", type: "warning", problem: true },
  email_error: { label: "Échec email", type: "error", problem: true },
  email_unsent: { label: "Email non envoyé", type: "warning", problem: true },
  cancelled: { label: "Annulée", type: "default", problem: false },
};

const ACCESS_LABEL: Record<string, string> = {
  GUEST: "Client",
  MEMBER: "Membre",
  ADMIN: "Admin",
  OWNER: "Propriétaire",
};

// Client-account integrity issue types -> badge label/color
const ISSUE_META: Record<string, { label: string; type: StatusType }> = {
  orphan_link: { label: "Compte supprimé", type: "error" },
  inactive_link: { label: "Doublon désactivé", type: "warning" },
  no_provider: { label: "Sans connexion", type: "warning" },
  not_guest: { label: "Sans accès", type: "info" },
};

const ACTION_LABEL: Record<string, string> = {
  repoint: "Relier au bon compte",
  send_login_email: "Envoyer un email de connexion",
  add_guest: "Donner l'accès",
  reinvite: "À réinviter",
};

function invKind(inv: InvitationResponse): Kind {
  if (inv.status === "cancelled") return "cancelled";
  if (inv.status === "accepted") return "accepted";
  if (inv.email_error) return "email_error";
  if (inv.email_sent === false) return "email_unsent";
  if (inv.expires_at && new Date(inv.expires_at).getTime() < Date.now()) return "expired";
  return "pending";
}

/** Format an ISO datetime string as "12 juin 2026, 14:30" (— if missing/invalid). */
function fmt(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PROVIDER_LABEL: Record<string, string> = {
  "google.com": "Google",
  "apple.com": "Apple",
  password: "Email / mot de passe",
  phone: "Téléphone",
};

/** Human-readable list of sign-in providers, e.g. "Google" or "Google · Email". */
function providerLabel(providers?: string[] | null): string {
  if (!providers || providers.length === 0) return "—";
  return providers.map((p) => PROVIDER_LABEL[p] || p).join(" · ");
}

// A user is "En ligne" only if they were active in the last few minutes.
// The stored `activity.connected` boolean is unreliable — it's set to true on
// login and never cleared on disconnect (the backend's set_user_offline is dead
// code), so it stays true for months. Derive presence from last-activity
// recency instead.
const ONLINE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

/** Milliseconds since epoch from a last-active value (ISO string from the API,
 * or a Firestore Timestamp `{seconds}`), or null if absent/unparseable. */
function lastActiveMs(la: any): number | null {
  if (!la) return null;
  if (typeof la === "string") {
    const t = new Date(la).getTime();
    return isNaN(t) ? null : t;
  }
  if (typeof la === "object" && typeof la.seconds === "number") return la.seconds * 1000;
  if (typeof la === "number") return la;
  return null;
}

function connMeta(user?: UserResponse): { label: string; cls: string } {
  if (!user) return { label: "Aucun compte", cls: "text-neutral-400" };
  if (user.status === "pending") return { label: "Jamais connecté", cls: "text-amber-500" };
  const la = (user.activity?.last_active_time as any) || user.last_active;
  const ms = lastActiveMs(la);
  if (ms !== null) {
    const age = Date.now() - ms;
    if (age >= 0 && age < ONLINE_WINDOW_MS)
      return { label: "En ligne", cls: "text-emerald-500" };
    return { label: `Actif ${getHistoryDateFromFlutter(la)}`, cls: "text-neutral-500" };
  }
  return { label: "Jamais connecté", cls: "text-amber-500" };
}

export const InvitationsMonitor = () => {
  const userData = useDataStore((state) => state.userData);
  const [invitations, setInvitations] = useState<InvitationResponse[]>([]);
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"invitations" | "clients">("invitations");
  const [search, setSearch] = useState("");
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [selected, setSelected] = useState<InvitationResponse | null>(null);
  const [selectedClient, setSelectedClient] = useState<UserResponse | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [integrity, setIntegrity] = useState<IntegrityReport | null>(null);
  const [integrityLoading, setIntegrityLoading] = useState(false);

  const load = async () => {
    if (!userData?.org_id) {
      // No org yet — don't leave the page stuck on "Chargement…".
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [inv, us] = await Promise.all([
        invitationsApi.list(),
        usersApi.list({ org_id: userData.org_id, limit: 1000 }),
      ]);
      setInvitations(inv);
      setUsers(us);
    } catch (error) {
      console.error("Failed to load invitations/clients:", error);
      showNotification({ type: "error", message: "Impossible de charger les invitations" });
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrity = async () => {
    if (!userData?.org_id) return;
    setIntegrityLoading(true);
    try {
      const rep = await invitationsApi.integrity();
      setIntegrity(rep);
    } catch (error) {
      console.error("Failed to load integrity report:", error);
      showNotification({
        type: "error",
        message: "Impossible de charger le rapport d'intégrité",
      });
    } finally {
      setIntegrityLoading(false);
    }
  };

  const doFix = async (issue: IntegrityIssue) => {
    if (!issue.fixable) return;
    setActionLoading(`fix:${issue.issue_id}`);
    try {
      await invitationsApi.fixIntegrity({
        project_id: issue.project_id,
        contact_index: issue.contact_index,
        action: issue.suggested_action as
          | "repoint"
          | "send_login_email"
          | "add_guest",
        target_user_id: issue.candidate_user_id,
      });
      showNotification({ type: "success", message: "Problème corrigé" });
      await loadIntegrity();
    } catch (error: any) {
      showNotification({
        type: "error",
        message: error?.response?.data?.detail || "Échec de la correction",
      });
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    load();
    // Load the integrity report in the background so each invitation/client
    // detail can surface (and fix) that account's issues.
    loadIntegrity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.org_id]);

  const usersById = useMemo(() => {
    const map: Record<string, UserResponse> = {};
    for (const u of users) map[u.id] = u;
    return map;
  }, [users]);

  const nameById = (id?: string) => {
    if (!id) return "—";
    const u = usersById[id];
    return u?.infos.full_name || u?.infos.display_name || id;
  };

  const s = search.trim().toLowerCase();
  const matchInv = (inv: InvitationResponse) =>
    !s ||
    inv.invitee_email?.toLowerCase().includes(s) ||
    inv.invitee_name?.toLowerCase().includes(s) ||
    nameById(inv.invited_by_user_id).toLowerCase().includes(s);

  const invRows = useMemo(() => {
    return invitations
      .map((inv) => ({
        inv,
        kind: invKind(inv),
        user: inv.user_id ? usersById[inv.user_id] : undefined,
      }))
      .filter((r) => (onlyProblems ? KIND_META[r.kind].problem : true))
      .filter((r) => matchInv(r.inv))
      .sort((a, b) => (a.inv.created_at < b.inv.created_at ? 1 : -1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitations, usersById, onlyProblems, s]);

  const clients = useMemo(
    () => users.filter((u) => u.config?.access_type === "GUEST"),
    [users],
  );

  // Accounts flagged by the integrity report — the same source that drives the
  // "Problème compte" badge on each row, so the filter and the badge agree.
  const issueUserIds = useMemo(
    () => new Set((integrity?.issues || []).map((i) => i.user_id).filter(Boolean)),
    [integrity],
  );

  const clientRows = useMemo(() => {
    const isNever = (u: UserResponse) =>
      u.status === "pending" ||
      (!u.activity?.connected && !u.connected && !u.activity?.last_active_time && !u.last_active);
    return clients
      .filter((u) => (onlyProblems ? isNever(u) || issueUserIds.has(u.id) : true))
      .filter(
        (u) =>
          !s ||
          u.infos.full_name?.toLowerCase().includes(s) ||
          u.infos.display_name?.toLowerCase().includes(s) ||
          u.infos.email?.toLowerCase().includes(s),
      )
      .sort((a, b) =>
        (a.infos.full_name || a.infos.display_name || "").localeCompare(
          b.infos.full_name || b.infos.display_name || "",
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, onlyProblems, issueUserIds, s]);

  // Summary stats
  const stats = useMemo(() => {
    const kinds = invitations.map(invKind);
    return {
      clients: clients.length,
      neverConnected: clients.filter(
        (u) =>
          u.status === "pending" ||
          (!u.activity?.connected && !u.connected && !u.activity?.last_active_time && !u.last_active),
      ).length,
      pending: kinds.filter((k) => k === "pending").length,
      expired: kinds.filter((k) => k === "expired").length,
      emailProblems: kinds.filter((k) => k === "email_error" || k === "email_unsent").length,
    };
  }, [invitations, clients]);

  // --- Manual actions ---------------------------------------------------------
  const doResend = async (userId?: string) => {
    if (!userId) {
      showNotification({ type: "error", message: "Aucun compte lié à cette invitation" });
      return;
    }
    setActionLoading(`resend:${userId}`);
    try {
      await invitationsApi.resend(userId);
      showNotification({ type: "success", message: "Invitation renvoyée" });
      await load();
    } catch (error: any) {
      showNotification({
        type: "error",
        message: error?.response?.data?.detail || "Échec de l'envoi",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const doCancel = async (invitationId: string) => {
    setActionLoading(`cancel:${invitationId}`);
    try {
      await invitationsApi.cancel(invitationId);
      showNotification({ type: "success", message: "Invitation annulée" });
      setSelected(null);
      await load();
    } catch (error: any) {
      showNotification({
        type: "error",
        message: error?.response?.data?.detail || "Échec de l'annulation",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const copyLink = async (url?: string | null, successMsg = "Lien copié") => {
    if (!url) {
      showNotification({ type: "error", message: "Aucun lien disponible" });
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      showNotification({ type: "success", message: successMsg });
    } catch {
      showNotification({ type: "error", message: "Impossible de copier le lien" });
    }
  };

  // Reactivate a "used" invitation: issues a fresh link (no email) and copies
  // it to the clipboard so the admin can share it however they like.
  const doReopen = async (invitationId: string) => {
    setActionLoading(`reopen:${invitationId}`);
    try {
      const updated = await invitationsApi.reopen(invitationId);
      if (updated.invitation_url) {
        await copyLink(updated.invitation_url, "Lien réactivé et copié");
      } else {
        showNotification({ type: "success", message: "Invitation réactivée" });
      }
      setSelected(null);
      await load();
    } catch (error: any) {
      showNotification({
        type: "error",
        message: error?.response?.data?.detail || "Échec de la réactivation",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const doResetPassword = async (userId?: string) => {
    if (!userId) return;
    setActionLoading(`pwd:${userId}`);
    try {
      await usersApi.sendPasswordReset(userId);
      showNotification({ type: "success", message: "Email de réinitialisation envoyé" });
    } catch (error: any) {
      showNotification({
        type: "error",
        message: error?.response?.data?.detail || "Échec de l'envoi",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // --- Render -----------------------------------------------------------------
  const StatCard = ({ label, value, danger }: { label: string; value: number; danger?: boolean }) => (
    <div className="flex flex-col bg-white border rounded-lg px-4 py-3 min-w-140 flex-1">
      <div className={`text-h1 font-bold ${danger && value > 0 ? "text-red-500" : "text-black"}`}>
        {value}
      </div>
      <div className="text-h4 text-neutral-400">{label}</div>
    </div>
  );

  // Integrity issues for one account (user_id), rendered inside a detail view.
  // A contact can be broken across several projects, so we list each.
  const IntegritySection = ({ userId }: { userId?: string }) => {
    if (!userId) return null;
    const issues = (integrity?.issues || []).filter((i) => i.user_id === userId);
    return (
      <div className="flex flex-col gap-2 pt-3 border-t">
        <div className="text-h4 font-medium text-neutral-500">
          Intégrité du compte{integrityLoading ? " · analyse…" : ""}
        </div>
        {!integrity ? (
          <div className="text-h5 text-neutral-400">Analyse en cours…</div>
        ) : issues.length === 0 ? (
          <div className="text-h5 text-emerald-500">Aucun problème détecté.</div>
        ) : (
          issues.map((issue) => {
            const meta =
              ISSUE_META[issue.issue_type] || {
                label: issue.issue_type,
                type: "default" as StatusType,
              };
            return (
              <div
                key={issue.issue_id}
                className="flex flex-row items-start justify-between gap-2 bg-neutral-50 rounded-lg px-3 py-2"
              >
                <div className="flex flex-col min-w-0 flex-1 gap-1">
                  <div className="flex flex-row items-center gap-2 flex-wrap">
                    <StatusBlock label={meta.label} type={meta.type} />
                    <span className="text-h5 text-neutral-400 truncate">
                      Projet : {issue.project_name}
                    </span>
                  </div>
                  <div className="text-h5 text-neutral-400">{issue.detail}</div>
                </div>
                {issue.fixable ? (
                  <SecondaryButton
                    variant="small"
                    label={ACTION_LABEL[issue.suggested_action] || "Corriger"}
                    loading={actionLoading === `fix:${issue.issue_id}`}
                    onClick={() => doFix(issue)}
                  />
                ) : (
                  <span className="text-h5 text-neutral-400 whitespace-nowrap">
                    {ACTION_LABEL[issue.suggested_action] || "Manuel"}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-900">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Invitations & clients</Text1>
          <Text2>Suivez les invitations envoyées et l'activité des clients.</Text2>
          <Text3>
            Repérez les invitations en échec ou expirées, voyez la dernière connexion de chaque
            client et corrigez les problèmes (renvoyer, annuler une invitation).
          </Text3>
        </div>

        {/* Summary */}
        <div className="flex flex-row flex-wrap gap-3">
          <StatCard label="Clients" value={stats.clients} />
          <StatCard label="Jamais connectés" value={stats.neverConnected} danger />
          <StatCard label="Invitations en attente" value={stats.pending} />
          <StatCard label="Expirées" value={stats.expired} danger />
          <StatCard label="Problèmes email" value={stats.emailProblems} danger />
        </div>

        <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border">
          {/* Tabs */}
          <div className="flex flex-row gap-2">
            {(["invitations", "clients"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-1.5 rounded-lg text-h3 font-medium ${
                  tab === t
                    ? "bg-black text-white"
                    : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                }`}
              >
                {t === "invitations"
                  ? `Invitations (${invitations.length})`
                  : `Clients (${clients.length})`}
              </button>
            ))}
            <div className="flex-1" />
            <label className="flex flex-row items-center gap-2 text-h4 text-neutral-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyProblems}
                onChange={(e) => setOnlyProblems(e.target.checked)}
              />
              Problèmes uniquement
            </label>
          </div>

          <SearchField
            value={search}
            onChange={(value) => setSearch(value)}
            placeholder="Rechercher par nom ou email…"
          />

          {loading ? (
            <div className="text-h4 text-neutral-400 text-center py-8">Chargement…</div>
          ) : tab === "invitations" ? (
            invRows.length === 0 ? (
              <div className="text-h4 text-neutral-400 text-center py-8">
                Aucune invitation à afficher
              </div>
            ) : (
              <div className="flex flex-col divide-y divide-neutral-200 max-h-[60vh] overflow-y-auto">
                {invRows.map(({ inv, kind, user }) => {
                  const meta = KIND_META[kind];
                  const conn = connMeta(user);
                  return (
                    <div
                      key={inv.id}
                      className="flex flex-row justify-between items-center py-3 px-2 gap-3 hover:bg-neutral-50 cursor-pointer"
                      onClick={() => setSelected(inv)}
                    >
                      <div className="flex flex-col min-w-0 flex-1">
                        <div className="text-h3 font-medium truncate">
                          {inv.invitee_name || inv.invitee_email}
                        </div>
                        <div className="text-h4 text-neutral-400 truncate">{inv.invitee_email}</div>
                      </div>
                      <div className="hidden md:flex flex-col items-start w-160 flex-shrink-0">
                        <div className="text-h5 text-neutral-400">
                          Invité par {nameById(inv.invited_by_user_id)}
                        </div>
                        <div className={`text-h5 ${conn.cls}`}>{conn.label}</div>
                        {inv.user_providers && inv.user_providers.length > 0 && (
                          <div className="text-h5 text-neutral-400">
                            via {providerLabel(inv.user_providers)}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <StatusBlock label={meta.label} type={meta.type} />
                      </div>
                      <div className="flex flex-row gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        {kind !== "accepted" && kind !== "cancelled" && (
                          <SecondaryButton
                            variant="small"
                            label="Renvoyer"
                            loading={actionLoading === `resend:${inv.user_id}`}
                            onClick={() => doResend(inv.user_id)}
                          />
                        )}
                        {kind === "pending" && inv.invitation_url && (
                          <SecondaryButton
                            variant="small"
                            label="Copier le lien"
                            onClick={() => copyLink(inv.invitation_url)}
                          />
                        )}
                        {kind === "accepted" && (
                          <SecondaryButton
                            variant="small"
                            label="Réactiver le lien"
                            loading={actionLoading === `reopen:${inv.id}`}
                            onClick={() => doReopen(inv.id)}
                          />
                        )}
                        <SecondaryButton
                          variant="small"
                          label="Détails"
                          onClick={() => setSelected(inv)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : clientRows.length === 0 ? (
            <div className="text-h4 text-neutral-400 text-center py-8">Aucun client à afficher</div>
          ) : (
            <div className="flex flex-col divide-y divide-neutral-200 max-h-[60vh] overflow-y-auto">
              {clientRows.map((user) => {
                const conn = connMeta(user);
                const clientIssues = (integrity?.issues || []).filter(
                  (i) => i.user_id === user.id,
                );
                return (
                  <div
                    key={user.id}
                    className="flex flex-row justify-between items-center py-3 px-2 gap-3 hover:bg-neutral-50 cursor-pointer"
                    onClick={() => setSelectedClient(user)}
                  >
                    <div className="flex flex-row items-center gap-3 flex-1 min-w-0">
                      <UserAvatar
                        src={user.customization?.photo_url}
                        className="w-10 h-10 rounded-full border flex-shrink-0"
                        value={user.customization?.avatar_seed || user.infos.display_name}
                        size={40}
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <div className="text-h3 font-medium truncate">
                          {user.infos.full_name || user.infos.display_name}
                        </div>
                        <div className="text-h4 text-neutral-400 truncate">{user.infos.email}</div>
                      </div>
                    </div>
                    {clientIssues.length > 0 && (
                      <div className="flex-shrink-0">
                        <StatusBlock label="Problème compte" type="error" />
                      </div>
                    )}
                    <div className={`text-h5 flex-shrink-0 w-160 ${conn.cls}`}>{conn.label}</div>
                    <div
                      className="flex flex-row gap-2 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SecondaryButton
                        variant="small"
                        label="Renvoyer"
                        loading={actionLoading === `resend:${user.id}`}
                        onClick={() => doResend(user.id)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Detail / timeline modal */}
      {selected &&
        (() => {
          const kind = invKind(selected);
          const meta = KIND_META[kind];
          const ctx = selected.invitation_context || selected.context || {};
          const user = selected.user_id ? usersById[selected.user_id] : undefined;
          const conn = connMeta(user);
          const projectId = ctx.project_id as string | undefined;
          return (
            <BaseModal title="Détail de l'invitation" onClose={() => setSelected(null)}>
              <div className="flex flex-col gap-4 p-4 w-full max-w-500">
                <div className="flex flex-row items-center justify-between gap-2">
                  <div className="flex flex-col min-w-0">
                    <div className="text-h2 font-semibold truncate">
                      {selected.invitee_name || selected.invitee_email}
                    </div>
                    <div className="text-h4 text-neutral-400 truncate">{selected.invitee_email}</div>
                  </div>
                  <StatusBlock label={meta.label} type={meta.type} />
                </div>

                {/* Meta grid */}
                <div className="grid grid-cols-2 gap-3">
                  <Info label="Type" value={selected.invitation_type === "mobile" ? "Mobile (code)" : "Web (lien)"} />
                  <Info label="Accès" value={ACCESS_LABEL[ctx.access_type] || ctx.access_type || "—"} />
                  <Info label="Invité par" value={nameById(selected.invited_by_user_id)} />
                  <Info label="Connexion" value={conn.label} valueCls={conn.cls} />
                  <Info label="Fournisseur" value={providerLabel(selected.user_providers)} />
                </div>

                {/* Timeline */}
                <div className="flex flex-col gap-2">
                  <div className="text-h4 font-medium text-neutral-500">Historique</div>
                  <TimelineRow label="Invitation créée" value={fmt(selected.created_at)} />
                  {selected.email_error ? (
                    <TimelineRow
                      label="Échec de l'envoi de l'email"
                      value={selected.email_error}
                      danger
                    />
                  ) : selected.email_sent === false ? (
                    <TimelineRow label="Email non envoyé" value="En attente d'envoi" danger />
                  ) : (
                    <TimelineRow label="Email envoyé" value={fmt(selected.email_sent_at)} />
                  )}
                  <TimelineRow
                    label={kind === "expired" ? "Expirée le" : "Expire le"}
                    value={fmt(selected.expires_at)}
                    danger={kind === "expired"}
                  />
                  {selected.accepted_at && (
                    <TimelineRow label="Invitation acceptée" value={fmt(selected.accepted_at)} success />
                  )}
                  {selected.status === "cancelled" && (
                    <TimelineRow label="Invitation annulée" value={fmt(selected.updated_at)} />
                  )}
                  {user && (
                    <TimelineRow label="Dernière connexion" value={conn.label} />
                  )}
                </div>

                {/* Account integrity — issues + one-click fixes for this account */}
                <IntegritySection userId={selected.user_id} />

                {/* Actions */}
                <div className="flex flex-row flex-wrap gap-2 pt-2 border-t">
                  {kind !== "accepted" && kind !== "cancelled" && (
                    <SecondaryButton
                      variant="small"
                      label="Renvoyer l'invitation"
                      loading={actionLoading === `resend:${selected.user_id}`}
                      onClick={() => doResend(selected.user_id)}
                    />
                  )}
                  {kind === "accepted" && (
                    <SecondaryButton
                      variant="small"
                      label="Réactiver le lien"
                      loading={actionLoading === `reopen:${selected.id}`}
                      onClick={() => doReopen(selected.id)}
                    />
                  )}
                  {selected.invitation_type === "web" && selected.invitation_url && kind !== "accepted" && (
                    <SecondaryButton
                      variant="small"
                      label="Copier le lien"
                      onClick={() => copyLink(selected.invitation_url)}
                    />
                  )}
                  {selected.status === "pending" && (
                    <SecondaryButton
                      variant="small"
                      danger
                      label="Annuler l'invitation"
                      loading={actionLoading === `cancel:${selected.id}`}
                      onClick={() => doCancel(selected.id)}
                    />
                  )}
                  <SecondaryButton
                    variant="small"
                    label="Réinit. mot de passe"
                    loading={actionLoading === `pwd:${selected.user_id}`}
                    onClick={() => doResetPassword(selected.user_id)}
                  />
                  {projectId && (
                    <SecondaryButton
                      variant="small"
                      label="Voir le projet"
                      onClick={() => navigateTo({ route: `/project/${projectId}` })}
                    />
                  )}
                </div>
              </div>
            </BaseModal>
          );
        })()}

      {/* Client detail — same integrity + fixes, keyed to the client account */}
      {selectedClient &&
        (() => {
          const conn = connMeta(selectedClient);
          return (
            <BaseModal title="Détail du client" onClose={() => setSelectedClient(null)}>
              <div className="flex flex-col gap-4 p-4 w-full max-w-500">
                <div className="flex flex-row items-center gap-3">
                  <UserAvatar
                    src={selectedClient.customization?.photo_url}
                    className="w-12 h-12 rounded-full border flex-shrink-0"
                    value={
                      selectedClient.customization?.avatar_seed ||
                      selectedClient.infos.display_name
                    }
                    size={48}
                  />
                  <div className="flex flex-col min-w-0">
                    <div className="text-h2 font-semibold truncate">
                      {selectedClient.infos.full_name || selectedClient.infos.display_name}
                    </div>
                    <div className="text-h4 text-neutral-400 truncate">
                      {selectedClient.infos.email}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Info label="Connexion" value={conn.label} valueCls={conn.cls} />
                </div>

                <IntegritySection userId={selectedClient.id} />

                <div className="flex flex-row flex-wrap gap-2 pt-2 border-t">
                  <SecondaryButton
                    variant="small"
                    label="Réinit. mot de passe"
                    loading={actionLoading === `pwd:${selectedClient.id}`}
                    onClick={() => doResetPassword(selectedClient.id)}
                  />
                  <SecondaryButton
                    variant="small"
                    label="Renvoyer l'invitation"
                    loading={actionLoading === `resend:${selectedClient.id}`}
                    onClick={() => doResend(selectedClient.id)}
                  />
                </div>
              </div>
            </BaseModal>
          );
        })()}
    </div>
  );
};

const Info = ({ label, value, valueCls }: { label: string; value: string; valueCls?: string }) => (
  <div className="flex flex-col">
    <div className="text-h5 text-neutral-400">{label}</div>
    <div className={`text-h3 font-medium truncate ${valueCls || ""}`}>{value}</div>
  </div>
);

const TimelineRow = ({
  label,
  value,
  danger,
  success,
}: {
  label: string;
  value: string;
  danger?: boolean;
  success?: boolean;
}) => (
  <div className="flex flex-row items-start gap-2">
    <div
      className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
        danger ? "bg-red-500" : success ? "bg-emerald-500" : "bg-neutral-300"
      }`}
    />
    <div className="flex flex-col min-w-0 flex-1">
      <div className="text-h4 font-medium text-neutral-600">{label}</div>
      <div className={`text-h4 ${danger ? "text-red-500" : "text-neutral-400"} break-words`}>
        {value}
      </div>
    </div>
  </div>
);
